import React, { useState } from 'react';
import { Box, Typography, InputAdornment, IconButton, Alert, Link } from '@mui/material';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useFormik } from 'formik';
import { object, string, ref } from 'yup';
import { CustomTextField, CustomButton } from './CustomComponents';
import urbanImage from '../assets/urban_logo.png';

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

function ResetPassword(): React.ReactElement {
  const navigate = useNavigate();
  const { token = '', hash: emailHash = '' } = useParams<{ token?: string; hash?: string }>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [expiredLink, setExpiredLink] = useState(false);

  // The reset link should include a one-time unique token and the hashed email:
  // /reset-password/<token>/<hashedEmail>
  // Backend must verify both, ensure one-time use, and expire the link after 15 minutes.
  const formik = useFormik<ResetPasswordForm>({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: object({
      password: string()
        .required('La contraseña es obligatoria')
        .min(8, 'La contraseña debe tener al menos 8 caracteres'),
      confirmPassword: string()
        .required('Confirma tu contraseña')
        .oneOf([ref('password')], 'Las contraseñas no coinciden'),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setServerError(null);
      setServerMessage(null);
      setExpiredLink(false);
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/reset-password/${encodeURIComponent(token)}/${encodeURIComponent(emailHash)}`,
          {
            password: values.password,
          }
        );
        setServerMessage(
          'Tu contraseña se actualizó correctamente. Seras redirigido a la página de inicio de sesión.'
        );
        resetForm();
        setTimeout(() => navigate('/login'), 5000);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 410) {
          setExpiredLink(true);
          setServerError('El enlace ha expirado o ya fue utilizado, por favor solicita uno nuevo.');
        } else {
          setServerError(
            'No se pudo restablecer la contraseña. Verifica el enlace o inténtalo nuevamente.'
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // compute alert severity without nested ternary to satisfy ESLint
  let alertSeverity: 'success' | 'warning' | 'error' = 'error';
  if (serverMessage) {
    alertSeverity = 'success';
  } else if (expiredLink) {
    alertSeverity = 'warning';
  } else {
    alertSeverity = 'error';
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '40px 20px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          minHeight: '450px',
          maxWidth: 600,
          padding: '0 40px 40px 40px',
          borderRadius: 4,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <img
            src={urbanImage}
            alt="Urban Utility Logo"
            style={{
              maxWidth: '100%',
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Restablecer contraseña
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 2, fontSize: '14px' }}>
          Crea una nueva contraseña para tu cuenta. Ingresa y confirma la nueva contraseña.
        </Typography>

        {(serverError || serverMessage) && (
          <Alert severity={alertSeverity} sx={{ mb: 2 }}>
            {serverMessage || serverError}
            {expiredLink && (
              <Typography sx={{ mt: 1, fontSize: 14 }}>
                <Link href="/recover-password" underline="hover" sx={{ fontWeight: 700 }}>
                  Solicitar nuevo enlace de recuperación de contraseña
                </Link>
              </Typography>
            )}
          </Alert>
        )}

        <Box
          component="form"
          autoComplete="off"
          noValidate
          onSubmit={formik.handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <CustomTextField
            fullWidth
            required
            id="password"
            name="password"
            label="Nueva contraseña"
            placeholder="••••••••"
            type={showPassword ? 'text' : 'password'}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <CustomTextField
            fullWidth
            required
            id="confirmPassword"
            name="confirmPassword"
            label="Confirmar contraseña"
            placeholder="••••••••"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showConfirmPassword ? 'Ocultar confirmación' : 'Mostrar confirmación'
                      }
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <CustomButton fullWidth type="submit" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? 'Restableciendo...' : 'Guardar nueva contraseña'}
          </CustomButton>

          {/* Success/error alerts are shown above the form; keep form area clean */}
        </Box>
      </Box>
    </Box>
  );
}

export default ResetPassword;
